import {
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import axios from "axios";
import * as React from "react";

import { LoadingWrapper } from "../../../components/LoadingWrapper";
import { Config } from "../../../config/config";
import { Helpers } from "../../../utils/helpers";
import { PopupMessage } from "../../../utils/message";

interface AllianceWarSeason {
    id: string;
    name: string;
    status: string;
    configId: number;
    rewardConfigId: number;
    startDate: string;
    endDate: string;
}

export const AllianceWarPreparations = (props: any) => {
    const pageSize = 10;
    const [seasons, setSeasons] = React.useState<AllianceWarSeason[]>([]);
    const [page, setPage] = React.useState(0);
    const [hasMore, setHasMore] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        document.title = Helpers.documentTitle("Alliance Wars / Preparations");
        loadSeasons(0);
    }, []);

    const loadSeasons = (nextPage: number) => {
        if (nextPage < 0) {
            return;
        }

        setLoading(true);
        setError(null);

        axios.get(`${Config.getInstance.apiUrl}/api/alliance_wars/seasons`, {
            headers: {
                Authorization: Helpers.getSessionId(),
            },
            params: {
                limit: pageSize,
                offset: nextPage * pageSize,
            },
        })
            .then((response) => {
                if (!response.data?.success) {
                    setError("Failed to fetch alliance war seasons");
                    PopupMessage.error("Failed to fetch alliance war seasons");
                    return;
                }

                const list = response.data.seasons || [];
                setSeasons(list);
                setPage(nextPage);
                setHasMore(list.length === pageSize);
            })
            .catch((err) => {
                const message = err?.response?.data?.message || err?.message || "Failed to fetch alliance war seasons";
                setError(message);
                PopupMessage.error(message);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const formatDateTime = (dateString?: string) => {
        if (!dateString) {
            return "-";
        }

        return Helpers.formatDateWithTime(dateString, true);
    };

    const openPreparations = (seasonId: string) => {
        props.history.push(`/alliance_wars/preparations/${seasonId}`);
    };

    return (
        <div className="contentPadding">
            <Paper className="root" elevation={0}>
                <div style={{ padding: "15px 22px" }}>
                    <Typography variant="h6">Alliance wars / Preparations</Typography>
                </div>
            </Paper>

            <Paper className="root" elevation={0} style={{ marginTop: 16, padding: 22 }}>
                <LoadingWrapper loading={loading} error={error}>
                    {seasons.length === 0 ? (
                        <Typography>No season data</Typography>
                    ) : (
                        <React.Fragment>
                            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
                                <Button size="small" disabled={page === 0} onClick={() => loadSeasons(page - 1)}>
                                    Prev
                                </Button>
                                <Typography variant="body2">Season Page {page + 1}</Typography>
                                <Button size="small" disabled={!hasMore} onClick={() => loadSeasons(page + 1)}>
                                    Next
                                </Button>
                            </div>

                            <Table className="min-table-size">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Season</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Config</TableCell>
                                        <TableCell>Time</TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {seasons.map((seasonItem) => (
                                        <TableRow hover key={seasonItem.id}>
                                            <TableCell>
                                                <Typography variant="body2">{seasonItem.name}</Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {seasonItem.id}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{seasonItem.status || "-"}</TableCell>
                                            <TableCell>
                                                {`${seasonItem.configId} - ${seasonItem.rewardConfigId}`}
                                            </TableCell>
                                            <TableCell>
                                                {formatDateTime(seasonItem.startDate)} - {formatDateTime(seasonItem.endDate)}
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="primary"
                                                    disableElevation
                                                    onClick={() => openPreparations(seasonItem.id)}
                                                >
                                                    View preparations
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </React.Fragment>
                    )}
                </LoadingWrapper>
            </Paper>
        </div>
    );
};
